import { useQuery } from '@tanstack/react-query';
import { Spinner, PageSkeleton } from '@daycal/ui';
import { api } from '../lib/apiClient.js';
import { FiCalendar, FiClock } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext.jsx';

export default function Dashboard() {
  const { data, isLoading } = useQuery({ queryKey: ['kpis'], queryFn: api.bookingKpis });
  const { profile } = useAuth();

  if (isLoading) return <PageSkeleton />;
  const breakdown = data?.status_breakdown || {};
  const total = Object.values(breakdown).reduce((a, b) => a + b, 0);

  const statuses = [
    { key: 'confirmed', label: 'Confirmed', color: 'bg-emerald-500', hex: '#10b981', count: breakdown.confirmed || 0 },
    { key: 'completed', label: 'Completed', color: 'bg-blue-500', hex: '#3b82f6', count: breakdown.completed || 0 },
    { key: 'cancelled', label: 'Cancelled', color: 'bg-red-500', hex: '#ef4444', count: breakdown.cancelled || 0 },
    { key: 'pending', label: 'Pending', color: 'bg-amber-500', hex: '#f59e0b', count: breakdown.pending || 0 },
    { key: 'awaiting_payment', label: 'Awaiting Payment', color: 'bg-slate-400', hex: '#94a3b8', count: breakdown.awaiting_payment || 0 },
  ];

  // Donut segments: circumference is normalized to 100 (r = 100 / 2π) so each
  // segment's dash length is just its percentage of total. Rendered clockwise
  // from 12 o'clock (the -90° rotation on the <svg>).
  const R = 15.915;
  let dashOffset = 0;
  const segments = total === 0 ? [] : statuses
    .filter((s) => s.count > 0)
    .map((s) => {
      const pct = (s.count / total) * 100;
      const seg = { ...s, pct, offset: dashOffset };
      dashOffset += pct;
      return seg;
    });

  // Monthly booking usage vs. plan cap (migration 13). Only shown on capped plans (-1 = unlimited).
  const monthlyCap = profile?.max_monthly_bookings;
  const monthlyUsed = profile?.monthly_bookings_used ?? 0;
  const showQuota = typeof monthlyCap === 'number' && monthlyCap !== -1;
  const quotaPct = showQuota && monthlyCap > 0 ? Math.min(100, Math.round((monthlyUsed / monthlyCap) * 100)) : 0;
  const quotaFull = showQuota && monthlyUsed >= monthlyCap;
  const quotaNear = showQuota && !quotaFull && quotaPct >= 80;
  const quotaColor = quotaFull ? 'bg-red-500' : quotaNear ? 'bg-amber-500' : 'bg-emerald-500';

  // Headline stats (live from /bookings/kpis). Revenue is integer paise → ₹ via en-IN.
  const todayCount = data?.today ?? 0;
  const upcomingCount = data?.upcoming ?? 0;
  const revenue = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' })
    .format((Number(data?.revenue_minor) || 0) / 100);

  return (
    <div className="animate-dc-rise space-y-6">
      {/* Header Block */}
      <div className="rounded-3xl bg-white p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Bookings & Overview</h1>
          <p className="mt-1 text-sm text-slate-500">See your upcoming appointments, revenue metrics, and booking status.</p>
        </div>
      </div>

      {/* Monthly booking quota (capped plans only) */}
      {showQuota && (
        <div className="rounded-3xl bg-white p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">Monthly Bookings</h2>
              <p className="mt-1 text-sm text-slate-500">Resets on the 1st of each month.</p>
            </div>
            <div className="text-right">
              <span className="text-2xl font-extrabold text-slate-900">{monthlyUsed}</span>
              <span className="text-sm font-semibold text-slate-400"> / {monthlyCap}</span>
            </div>
          </div>
          <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className={`h-full ${quotaColor} transition-all`} style={{ width: `${quotaPct}%` }} />
          </div>
          {(quotaFull || quotaNear) && (
            <p className={`mt-3 text-xs font-semibold ${quotaFull ? 'text-red-600' : 'text-amber-600'}`}>
              {quotaFull
                ? 'You’ve reached your monthly booking limit — new bookings are paused. Upgrade your plan to accept more.'
                : 'You’re close to your monthly booking limit. Consider upgrading to avoid interruptions.'}
            </p>
          )}
        </div>
      )}

      {/* 3 Column Stats */}
      <div className="grid gap-6 md:grid-cols-3">
        <div className="rounded-3xl bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">Today's Bookings</h2>
            <div className="grid h-8 w-8 place-items-center rounded-full bg-slate-50 text-slate-600">
              <FiCalendar className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-4 text-4xl font-extrabold text-slate-900">{todayCount}</div>
        </div>

        <div className="rounded-3xl bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">Upcoming Bookings</h2>
            <div className="grid h-8 w-8 place-items-center rounded-full bg-slate-50 text-slate-600">
              <FiClock className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-4 text-4xl font-extrabold text-slate-900">{upcomingCount}</div>
        </div>

        <div className="rounded-3xl bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Revenue</h2>
            <div className="grid h-8 w-8 place-items-center rounded-full bg-emerald-50 text-emerald-600 font-bold text-base">
              ₹
            </div>
          </div>
          <div className="mt-4 text-4xl font-extrabold text-slate-900">{revenue}</div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Status Distribution */}
        <div className="flex flex-col rounded-3xl bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100 min-h-[400px]">
          <h2 className="text-sm font-bold text-slate-900">STATUS DISTRIBUTION</h2>
          <p className="mt-1 text-xs text-slate-500">Visual proportion of appointment statuses.</p>
          
          <div className="flex flex-1 items-center justify-center pt-8">
            {total === 0 ? (
              <div className="flex flex-col items-center gap-3 text-slate-400">
                <div className="grid h-40 w-40 place-items-center rounded-full border-8 border-slate-100">
                  <span className="text-xs font-semibold">No data</span>
                </div>
                <p className="text-xs">No appointments yet.</p>
              </div>
            ) : (
              <div className="relative h-48 w-48">
                <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
                  <circle cx="18" cy="18" r={R} fill="none" stroke="#f1f5f9" strokeWidth="4" />
                  {segments.map((s) => (
                    <circle
                      key={s.key}
                      cx="18"
                      cy="18"
                      r={R}
                      fill="none"
                      stroke={s.hex}
                      strokeWidth="4"
                      strokeDasharray={`${s.pct} ${100 - s.pct}`}
                      strokeDashoffset={-s.offset}
                    />
                  ))}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-extrabold text-slate-900">{total}</span>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Appointments</span>
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-auto text-center text-[10px] font-bold uppercase tracking-wider text-slate-400">
            TOTAL APPOINTMENTS: {total}
          </div>
        </div>

        {/* Status Overview Report */}
        <div className="rounded-3xl bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100 min-h-[400px]">
          <h2 className="text-sm font-bold text-slate-900">STATUS OVERVIEW REPORT</h2>
          <p className="mt-1 text-xs text-slate-500">Live breakdown and comparative percentage stats.</p>
          
          <div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-6">
            {statuses.map((s) => {
              const percentage = total === 0 ? 0 : Math.round((s.count / total) * 100);
              return (
                <div key={s.key} className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${s.color}`} />
                      <span className="text-xs font-semibold text-slate-700">{s.label}</span>
                    </div>
                    <span className="text-xs font-bold text-slate-900">{s.count}</span>
                  </div>
                  
                  <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className={`h-full ${s.color}`} style={{ width: `${percentage}%` }} />
                  </div>
                  
                  <div className="text-[10px] text-slate-400">
                    {percentage}% of total bookings
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
